import React, { useState } from 'react';
import { theme } from '../../styles/theme';

const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  disabled = false, 
  loading = false,
  fullWidth = false,
  onClick, 
  type = 'button',
  className = '',
  style = {},
  ...props 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const getButtonStyles = () => {
    const baseStyles = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.sm,
      fontFamily: theme.typography.fontFamily.primary,
      fontWeight: theme.typography.fontWeight.semibold,
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: theme.transitions.normal,
      border: 'none',
      outline: 'none',
      textDecoration: 'none',
      width: fullWidth ? '100%' : 'auto',
      position: 'relative',
      overflow: 'hidden',
    };

    // Variantes
    const variants = {
      primary: {
        backgroundColor: disabled ? theme.colors.gray[400] : theme.colors.primary,
        color: theme.colors.white,
      },
      secondary: {
        backgroundColor: disabled ? theme.colors.gray[400] : theme.colors.secondary,
        color: theme.colors.white,
      },
      outline: {
        backgroundColor: 'transparent',
        color: disabled ? theme.colors.gray[400] : theme.colors.primary,
        border: `2px solid ${disabled ? theme.colors.gray[400] : theme.colors.primary}`,
      },
      ghost: {
        backgroundColor: 'transparent',
        color: disabled ? theme.colors.gray[400] : theme.colors.primary,
      },
    };

    // Tamanhos
    const sizes = {
      sm: {
        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
        fontSize: theme.typography.fontSize.sm,
        borderRadius: theme.borderRadius.sm,
      },
      md: {
        padding: `${theme.spacing.md} ${theme.spacing.xl}`,
        fontSize: theme.typography.fontSize.base,
        borderRadius: theme.borderRadius.md,
      },
      lg: {
        padding: `${theme.spacing.lg} ${theme.spacing['2xl']}`,
        fontSize: theme.typography.fontSize.lg,
        borderRadius: theme.borderRadius.lg,
      },
    };

    // Estados hover e active
    let hoverStyles = {};
    let activeStyles = {};

    if (!disabled) {
      switch (variant) {
        case 'primary':
          if (isHovered) {
            hoverStyles = {
              backgroundColor: theme.colors.primaryDark,
              transform: 'translateY(-1px)',
              boxShadow: theme.shadows.lg,
            };
          }
          if (isActive) {
            activeStyles = {
              transform: 'translateY(0)',
            };
          }
          break;
        case 'secondary':
          if (isHovered) {
            hoverStyles = {
              backgroundColor: theme.colors.secondaryDark,
              transform: 'translateY(-1px)',
              boxShadow: theme.shadows.lg,
            };
          }
          break;
        case 'outline':
          if (isHovered) {
            hoverStyles = {
              backgroundColor: theme.colors.primary,
              color: theme.colors.white,
            };
          }
          break;
        case 'ghost':
          if (isHovered) {
            hoverStyles = {
              backgroundColor: `${theme.colors.primary}10`,
            };
          }
          break;
      }
    }

    return {
      ...baseStyles,
      ...variants[variant],
      ...sizes[size],
      ...hoverStyles,
      ...activeStyles,
      opacity: disabled ? 0.6 : 1,
      ...style,
    };
  };

  const handleClick = (e) => {
    if (!disabled && !loading && onClick) {
      onClick(e);
    }
  };

  const handleMouseEnter = () => {
    if (!disabled) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setIsActive(false);
  };

  const handleMouseDown = () => {
    if (!disabled) {
      setIsActive(true);
    }
  };

  const handleMouseUp = () => {
    setIsActive(false);
  };

  return (
    <button
      type={type}
      style={getButtonStyles()}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      disabled={disabled || loading}
      className={className}
      {...props}
    >
      {loading && (
        <div style={{
          width: '16px',
          height: '16px',
          border: '2px solid transparent',
          borderTop: '2px solid currentColor',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
      )}
      {children}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </button>
  );
};

export default Button; 